import { apiClient } from './client';
import type { 
  BankTransferConfigRequest, 
  BankTransferConfigResponse,
  BankTransferApiResponse 
} from './types/bankTransfer';

/**
 * Bank Transfer Configuration API Service
 * Handles all bank transfer configuration operations for companies
 */

/**
 * Get current bank transfer configuration for the authenticated company
 */
export const getBankTransferConfig = async (): Promise<BankTransferApiResponse<BankTransferConfigResponse | null>> => {
  try {
    const response = await apiClient.get<BankTransferConfigResponse>('/payment/bank-transfer-config/');
    
    return {
      success: true,
      data: response,
    };
  } catch (error) {
    console.error('Error fetching bank transfer config:', error);
    return {
      success: false,
      error: 'Error al obtener la configuración de transferencia bancaria',
    };
  }
};

/**
 * Create new bank transfer configuration
 */
export const createBankTransferConfig = async (
  config: BankTransferConfigRequest
): Promise<BankTransferApiResponse<BankTransferConfigResponse>> => {
  try {
    const response = await apiClient.post<BankTransferConfigResponse>('/payment/bank-transfer-config/', config);
    
    return {
      success: true,
      data: response,
      message: 'Configuración de transferencia bancaria creada exitosamente',
    };
  } catch (error) {
    console.error('Error creating bank transfer config:', error);
    return {
      success: false,
      error: 'Error al crear la configuración de transferencia bancaria',
    };
  }
};

/**
 * Update existing bank transfer configuration
 */
export const updateBankTransferConfig = async (
  id: number,
  config: BankTransferConfigRequest
): Promise<BankTransferApiResponse<BankTransferConfigResponse>> => {
  try {
    const response = await apiClient.put<BankTransferConfigResponse>(`/payment/bank-transfer-config/${id}/`, config);
    
    return {
      success: true,
      data: response,
      message: 'Configuración de transferencia bancaria actualizada exitosamente',
    };
  } catch (error) {
    console.error('Error updating bank transfer config:', error);
    return {
      success: false,
      error: 'Error al actualizar la configuración de transferencia bancaria',
    };
  }
};

/**
 * Deactivate bank transfer configuration (soft delete)
 */
export const deactivateBankTransferConfig = async (
  id: number
): Promise<BankTransferApiResponse<void>> => {
  try {
    await apiClient.delete<void>(`/payment/bank-transfer-config/${id}/`);
    
    return {
      success: true,
      message: 'Configuración de transferencia bancaria desactivada exitosamente',
    };
  } catch (error) {
    console.error('Error deactivating bank transfer config:', error);
    return {
      success: false,
      error: 'Error al desactivar la configuración de transferencia bancaria',
    };
  }
};

/**
 * Reactivate bank transfer configuration
 */
export const reactivateBankTransferConfig = async (
  id: number
): Promise<BankTransferApiResponse<BankTransferConfigResponse>> => {
  try {
    const response = await apiClient.post<BankTransferConfigResponse>(`/payment/bank-transfer-config/${id}/reactivate/`, {});
    
    return {
      success: true,
      data: response,
      message: 'Configuración de transferencia bancaria reactivada exitosamente',
    };
  } catch (error) {
    console.error('Error reactivating bank transfer config:', error);
    return {
      success: false,
      error: 'Error al reactivar la configuración de transferencia bancaria',
    };
  }
};

/**
 * Validate CUIL format (11 digits)
 */
export const validateCUIL = (cuil: string): boolean => {
  if (!cuil || typeof cuil !== 'string') {
    return false;
  }
  const cleanCuil = cuil.replace(/\D/g, '');
  return cleanCuil.length === 11;
};

/**
 * Validate CBU format (22 digits)
 */
export const validateCBU = (cbu: string): boolean => {
  if (!cbu || typeof cbu !== 'string') {
    return false;
  }
  const cleanCbu = cbu.replace(/\D/g, '');
  return cleanCbu.length === 22;
};

/**
 * Format CUIL with dashes (XX-XXXXXXXX-X)
 */
export const formatCUIL = (cuil: string): string => {
  if (!cuil || typeof cuil !== 'string') {
    return cuil || '';
  }
  const cleanCuil = cuil.replace(/\D/g, '');
  if (cleanCuil.length === 11) {
    return `${cleanCuil.slice(0, 2)}-${cleanCuil.slice(2, 10)}-${cleanCuil.slice(10)}`;
  }
  return cuil;
};
